import React from 'react';
import StarRating from './StarRating';
import { formatForecastTime } from '../services/forecastTime';

interface FactorScores {
  temperature: number;
  front: number;
  wind: number;
  precipitation: number;
  clouds: number;
  pressure: number;
  moon: number;
  lunarFeeding: number;
}

interface PeriodConditions {
  tempF: number;
  windSpeedMph: number;
  windGustMph: number;
  cloudPercent: number;
  precipitationType: string;
  pressureTrend: string;
  frontType: string;
}

interface PeriodForecast {
  period: 'morning' | 'evening';
  rating: number;
  weightedScore: number;
  factors: FactorScores;
  conditions: PeriodConditions;
}

interface DayForecast {
  date: string;
  dayOfWeek: string;
  overallRating: number;
  sunrise: string;
  sunset: string;
  lunarPhase: string;
  lunarIllumination: number;
  morning: PeriodForecast;
  evening: PeriodForecast;
  morningSummary?: string;
  eveningSummary?: string;
}

interface DayCardProps {
  forecast: DayForecast;
  isToday: boolean;
  onDayClick?: (date: string) => void;
  dateLabel?: string;
}

function getRatingLabel(rating: number): string {
  const labels: { [key: number]: string } = {
    1: 'Poor',
    2: 'Below Average',
    3: 'Average',
    4: 'Good',
    5: 'Excellent',
  };
  return labels[rating] || 'Unknown';
}

function formatPrecip(type: string): string {
  if (type === 'none') return 'None';
  return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formatPressure(trend: string): string {
  return trend.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function formatFront(type: string): string {
  if (type === 'none') return 'None';
  return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function getBaromArrow(trend: string): string {
  if (trend === 'rapidly-rising' || trend === 'rising') return '↑';
  if (trend === 'rapidly-falling' || trend === 'falling') return '↓';
  return '―';
}

function getBaromLabel(trend: string): string {
  if (trend === 'rapidly-rising' || trend === 'rising') return 'Rising';
  if (trend === 'rapidly-falling' || trend === 'falling') return 'Falling';
  return 'Steady';
}

const FACTOR_LABELS: { [key: string]: { label: string; icon: string } } = {
  temperature: { label: 'Temperature', icon: '🌡️' },
  front: { label: 'Weather Front', icon: '🌊' },
  wind: { label: 'Wind', icon: '💨' },
  precipitation: { label: 'Precipitation', icon: '🌧️' },
  clouds: { label: 'Cloud Cover', icon: '☁️' },
  pressure: { label: 'Barometric', icon: '📊' },
  moon: { label: 'Moon Phase', icon: '🌙' },
  lunarFeeding: { label: 'Lunar Feeding', icon: '🌾' },
};

function FactorBar({ name, score }: { name: string; score: number }) {
  const info = FACTOR_LABELS[name] || { label: name, icon: '•' };
  const percentage = (score / 5) * 100;
  const color = score >= 4 ? 'var(--color-positive)' : score >= 3 ? 'var(--color-accent)' : 'var(--color-negative)';

  return (
    <div className="factor-bar-row">
      <span className="factor-bar-icon" aria-hidden="true">{info.icon}</span>
      <span className="factor-bar-label">{info.label}</span>
      <div className="factor-bar-track">
        <div className="factor-bar-fill" style={{ width: `${percentage}%`, backgroundColor: color }} />
      </div>
      <span className="factor-bar-score">{score.toFixed(1)}</span>
    </div>
  );
}

function PeriodSection({ periodData, time, summary }: { periodData: PeriodForecast; time: string; summary?: string }) {
  const { conditions, rating } = periodData;
  const periodLabel = periodData.period === 'morning' ? 'Sunrise' : 'Sunset';
  const periodIcon = periodData.period === 'morning' ? '🌅' : '🌇';

  return (
    <div className="period-section">
      <div className="period-header">
        <div className="period-title">
          <span aria-hidden="true">{periodIcon}</span>
          <h4>{periodLabel}</h4>
          <span className="period-time">{formatForecastTime(time)}</span>
        </div>
        <div className="period-rating">
          <StarRating rating={rating} size="medium" />
        </div>
      </div>

      {summary && <p className="period-summary">{summary}</p>}

      <div className="period-conditions">
        <div className="condition-chip">
          <span className="condition-label">Temp</span>
          <span className="condition-value">{conditions.tempF}°F</span>
        </div>
        <div className="condition-chip">
          <span className="condition-label">Wind</span>
          <span className="condition-value">{conditions.windSpeedMph} mph</span>
        </div>
        <div className="condition-chip">
          <span className="condition-label">Gusts</span>
          <span className="condition-value">{conditions.windGustMph} mph</span>
        </div>
        <div className="condition-chip">
          <span className="condition-label">Clouds</span>
          <span className="condition-value">{conditions.cloudPercent}%</span>
        </div>
        <div className="condition-chip">
          <span className="condition-label">Baro</span>
          <span className="condition-value">
            <span aria-hidden="true">{getBaromArrow(conditions.pressureTrend)}</span>
            <span className="sr-only">{getBaromLabel(conditions.pressureTrend)}</span>
          </span>
        </div>
        {conditions.precipitationType !== 'none' && (
          <div className="condition-chip condition-chip-alert">
            <span className="condition-label">Precip</span>
            <span className="condition-value">{formatPrecip(conditions.precipitationType)}</span>
          </div>
        )}
        {conditions.frontType !== 'none' && (
          <div className="condition-chip condition-chip-highlight">
            <span className="condition-label">Front</span>
            <span className="condition-value">{formatFront(conditions.frontType)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function DayCard({ forecast, isToday, onDayClick, dateLabel = formatDate(forecast.date) }: DayCardProps) {
  return (
    <article
      className={`day-card day-card-full ${isToday ? 'day-card-today' : ''} ${onDayClick ? 'day-card-clickable' : ''}`}
      onClick={() => onDayClick && onDayClick(forecast.date)}
    >
      <div className="day-card-header">
        <div className="day-info">
          <h3 className="day-name">
            {onDayClick ? (
              // Keyboard/screen-reader entry point; its click bubbles to the article handler.
              <button type="button" className="day-card-open" aria-label={`${isToday ? 'Today' : forecast.dayOfWeek}, ${dateLabel}: view hourly detail`}>
                {isToday ? 'Today' : forecast.dayOfWeek}
              </button>
            ) : (
              isToday ? 'Today' : forecast.dayOfWeek
            )}
          </h3>
          <span className="day-date">{dateLabel}</span>
        </div>
        <div className="rating-badge">
          <StarRating rating={forecast.overallRating} size={isToday ? 'large' : 'medium'} />
          <span className="rating-label">{getRatingLabel(forecast.overallRating)}</span>
        </div>
      </div>

      <div className="day-card-periods">
        <PeriodSection periodData={forecast.morning} time={forecast.sunrise} summary={forecast.morningSummary} />
        <PeriodSection periodData={forecast.evening} time={forecast.sunset} summary={forecast.eveningSummary} />
      </div>
    </article>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default DayCard;
